import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import ContributionSection from '../../components/ContributionSection/ContributionSection';
import ContributionSuccess from '../../components/ContributionSection/ContributionSuccess';
import ProfilePicture from '../../components/ProfilePicture/ProfilePicture';
import Icon from '../../components/Icon/Icon';
import Skeleton from '../../components/Skeleton/Skeleton';
import { fetchUserForContribution } from '../../Reducers/actions/userActions';
import { useSoftwall } from '../SoftWall/SoftWall';

import './ContributePage.scss';

/**
 * Normalizes username field from different API responses.
 * 
 * API Contract:
 * - Search API (fetchSearchUsers) returns: `username` (lowercase)
 * - Profile API (fetchUserById) returns: `userName` (camelCase)
 * 
 * This helper ensures consistent access regardless of source.
 * 
 * @param {Object} user - User object from API response
 * @returns {string|null} - Normalized username or null if not found
 */
const getNormalizedUsername = (user) => {
    if (!user) return null;
    return user.username || user.userName || null;
};

function ContributePageSkeleton() {
    return (
        <div role="status" aria-label="Loading creator">
            <div className="d-flex f-row aic jcsb px-16 py-24">
                <Skeleton width="220px" height="32px" borderRadius="16px" className="m-0 p-0" />
                <Skeleton width="72px" height="36px" borderRadius="20px" className="m-0 p-0" />
            </div>
            <div className="contribute-page">
                <div className="contribute-page-content d-flex f-column aic jcc">
                    <Skeleton width="56px" height="56px" borderRadius="50%" className="m-0 p-0" />
                    <Skeleton width="100%" height="32px" borderRadius="20px" className="m-0 p-0" />
                    <Skeleton width="90%" height="14px" borderRadius="6px" className="m-0 p-0" />
                    <Skeleton width="72%" height="14px" borderRadius="6px" className="m-0 p-0" />
                </div>
            </div>
        </div>
    );
}

function ContributePage() {
    const { username } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const sw = useSoftwall();

    // Local state for contribution user (separate from Redux view_user)
    // This allows the page to work for logged-out users

    const [showContribution, setShowContribution] = useState(true);
    const [showSuccessContribution, setShowSuccessContribution] = useState(searchParams.get('success') === 'true');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [contributionUser, setContributionUser] = useState(null);

    // Clean username - remove @ if present
    const cleanUsername = username?.replace(/^@/, '');

    // Fetch user for contribution when username changes
    useEffect(() => {
        // Cancellation flag to prevent state updates after unmount
        let cancelled = false;

        const fetchUser = async () => {
            if (!cleanUsername) {
                if (!cancelled) {
                    setError('Username is required');
                    setLoading(false);
                    setContributionUser(null);
                }
                return;
            }

            try {
                if (!cancelled) {
                    setLoading(true);
                    setError(null);
                    setContributionUser(null);
                }

                // Fetch user details using the new contribution endpoint
                // This endpoint works for both logged-in and logged-out users
                const response = await fetchUserForContribution(cleanUsername);

                // Early exit if component unmounted during async operation
                if (cancelled) return;

                // Handle different response structures
                // API might return: { success: true, data: {...} } or directly the user object
                let userData = null;
                
                if (response) {
                    // Check if response has success/data structure
                    if (response.success && response.data) {
                        userData = response.data;
                    } 
                    // Check if response is the user object directly
                    else if (response.user_id || response.username || response.userName) {
                        userData = response;
                    }
                    // Check if response.data exists (without success flag)
                    else if (response.data && (response.data.user_id || response.data.username || response.data.userName)) {
                        userData = response.data;
                    }
                }

                if (userData && !cancelled) {
                    // Verify the user matches the requested username
                    const normalizedUsername = getNormalizedUsername(userData);
                    const userMatches = normalizedUsername?.toLowerCase() === cleanUsername.toLowerCase() ||
                                       userData.user_id === cleanUsername ||
                                       userData.user_id?.toString() === cleanUsername;

                    if (userMatches) {
                        setContributionUser(userData);
                        setLoading(false);
                        setError(null);
                    } else {
                        setError('Creator not found');
                        setLoading(false);
                        setContributionUser(null);
                    }
                } else if (!cancelled) {
                    // Handle API error response
                    const errorMessage = response?.message || response?.error || 'Creator not found';
                    setError(errorMessage);
                    setLoading(false);
                    setContributionUser(null);
                }
            } catch (err) {
                if (!cancelled) {
                    // Handle network errors or unexpected errors
                    const errorMessage = err?.message || err?.response?.message || 'Failed to load creator information';
                    setError(errorMessage);
                    setLoading(false);
                    setContributionUser(null);
                }
            }
        };

        fetchUser();

        // Cleanup function to prevent state updates after unmount
        return () => {
            cancelled = true;
        };
    }, [cleanUsername]);


    const handleCloseContribution = () => {
        setShowContribution(false);
    };

    const handleCloseSuccess = () => {
        setShowSuccessContribution(false);
    };

    useEffect(() => {
        const handleResumeContribution = (event) => {
            const pendingIntent = event?.detail;
            if (pendingIntent?.source !== 'contribute') return;
            const intentCreatorId = pendingIntent?.creation?.creator_id;
            if (
                intentCreatorId &&
                contributionUser?.user_id &&
                String(intentCreatorId) !== String(contributionUser.user_id)
            ) {
                return;
            }
            setShowContribution(true);
        };

        window.addEventListener('hyk:resume-contribution', handleResumeContribution);
        return () => {
            window.removeEventListener('hyk:resume-contribution', handleResumeContribution);
        };
    }, [contributionUser?.user_id]);

    // Show loading state
    if (loading) {
        return <ContributePageSkeleton />;
    }

    // Show error state
    if (error && !loading) {
        return (
            <div className="contribute-page">
                <div className="contribute-page-error d-flex f-column jcc">
                    <Icon alias="alert" color="#FF3232" width={64} />
                    <h2 className="error-title">Creator Not Found</h2>
                    <p className="error-message">{error || 'The creator you are looking for does not exist.'}</p>
                    
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className='d-flex f-row aic jcsb px-16 py-24' >
                <div className='explore-culture d-flex f-row aic jcc' onClick={() => navigate('/')}>
                    <Icon alias="globe" color="#FF3232" width={20} />
                    <p className='fs-xs fw-bold ta-center'>Explore culture on</p>
                    <Icon alias="hyk-typography" color="#0A0F1E" width={33} />
                </div>
                {!sw.isAuthed && (
                    <button 
                        className='btn red-outline-btn d-flex f-row aic jcc'
                        onClick={() => sw?.openModal?.('signup')}
                        aria-label="Sign up"
                    >
                        sign up
                    </button>
                )}
            </div>
            <div className="contribute-page">
                <div className="contribute-page-content d-flex f-column aic jcc">
                    <div className="creator-info d-flex f-column aic">
                        <ProfilePicture
                            imgUrl={contributionUser?.profile_picture}
                            no_edit={true}
                            width={56}
                            height={56}
                            zoomEnable={true}
                        />
                    </div>

                    <button
                        className="btn discover-creations-btn py-8 aic jcc fw-extra-bold"
                        onClick={() => {
                            navigate(`/${encodeURIComponent(cleanUsername)}`);
                        }}
                    >
                        DISCOVER ALL MY CREATIONS
                    </button>
                    <p className="contribution-message ta-center">
                        Your contribution helps creators keep sharing magic – one creation at a time.
                    </p>
                </div>

                {showContribution && (
                    <ContributionSection
                        onClose={handleCloseContribution}
                        creation={{
                            creation_id: null,
                            title: `Creator ${getNormalizedUsername(contributionUser) || 'Creator'}`,
                            description: "Contribution to a creator",
                            creator_id: contributionUser?.user_id,
                            thumbnail_url: contributionUser?.profile_picture,
                            users: contributionUser
                        }}
                        allowBackgroundTouch={true}
                        twoSnapFeeSheet
                    />
                )}

                {showSuccessContribution && (
                    <ContributionSuccess onClose={handleCloseSuccess} />
                )}
            </div>
        </div>
    );
}

export default ContributePage;

